import { requestWebServiceTypes, responseWebServiceTypes } from './types'
import * as webServiceURL from './webServiceURL'
import useCallApi from '../../utilities/axios'
import {
    PendingAndResumeType,
    SynthesisReviewProjectRequestType,
    UpdateProjectDetailType,
} from './types/requestWebServiceTypes'
import useLoading from '../../control/loading/useLoading'
import type { AxiosRequestConfig } from 'axios'

export interface ExampleInfomation {
    filename: string
    path: string
    fileToken: string
    language: 'th' | 'en'
}

const useWebService = () => {
    const callApi = useCallApi()
    const loadingHook = useLoading()

    const example1 = async () => {
        const { data } = await callApi.axiosGet<any>({
            url: webServiceURL.example,
        })
        return data
    }
    const example2 = async () => {
        const { data } = await callApi.axiosGet<any>({
            url: webServiceURL.example,
        })
        return data
    }

    const createNewProject = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.createProject,
            params: params,
        })
        return data
    }

    const updateFileProject = async (params: requestWebServiceTypes.UpdateFileProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.updateFileProject,
            params: params,
        })
        return data
    }

    const updateFileStamp = async (params: requestWebServiceTypes.UpdateFileStampRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.updateFileStamp,
            params: params,
        })
        return data
    }

    const uploadFileTemp = async (params: FormData) => {
        try {
            loadingHook.loadingOpen()
            const { data } = await callApi.axiosPost<any>({
                url: webServiceURL.uploadTemp,
                params: params,
            })
            return data
        } catch (err) {
        } finally {
            loadingHook.loadingClose()
        }
    }

    const getProjectByID = async (projectID: number, submittedID: number) => {
        const url = `${webServiceURL.getProjectByID}/${projectID}/${submittedID}`
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetProjectResType>({
            url: url,
        })
        return data
    }

    const verifyProject = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProject,
            params: params,
        })
        return data
    }

    const verifyProjectAdverseEvent = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProjectAdverseEvent,
            params: params,
        })
        return data
    }

    const verifyProjectProgressReport = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProjectProgressReport,
            params: params,
        })
        return data
    }

    const verifyProjectSiteVisit = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProjectSiteVisit,
            params: params,
        })
        return data
    }

    const verifyProjectRenewal = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProjectRenewal,
            params: params,
        })
        return data
    }

    const verifyEditAfterReviewCloseProject = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyEditAfterReviewCloseProject,
            params: params,
        })
        return data
    }

    const verifyProjectTerminate = async (params: requestWebServiceTypes.VerifyProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.VerifyProjectResType>({
            url: webServiceURL.verifyProjectTerminate,
            params: params,
        })
        return data
    }

    const specifyProjectType = async (params: requestWebServiceTypes.SpecifyProjectTypeRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyProjectTypeType>({
            url: webServiceURL.specifyProjectType,
            params: params,
        })
        return data
    }

    const updSubmittedCommittee = async (params: requestWebServiceTypes.UpdSubmittedCommitteeType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.UpdSubmittedCommitteeType>({
            url: webServiceURL.updSubmittedCommittee,
            params: params,
        })
        return data
    }

    const searchTblMeeting = async (
        params: requestWebServiceTypes.ListRequestType,
        userGroupID: string,
        config?: AxiosRequestConfig,
    ) => {
        const { data } = await callApi.axiosPost<responseWebServiceTypes.GetListAllResultType>({
            url: webServiceURL.getListAllMeeting,
            params: { ...params, userGroupID },
            config,
        })
        return data
    }

    /** ผลการพิจารณาของหัวชุดรอบล่าสุด ใช้เติมช่องมติที่ประชุมตอนเลือกโครงการเข้าวาระ
     *  แยกจาก list-meeting เพราะข้อความยาว เรียกเฉพาะตอนกดยืนยัน */
    const getProjectReviewSummary = async (projectIDs: number[], config?: AxiosRequestConfig) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.getProjectReviewSummary,
            params: { projectIDs },
            config,
        })
        return data
    }

    const searchTbl = async (params: requestWebServiceTypes.ListRequestType, userGroupID: string) => {
        const { data } = await callApi.axiosPost<responseWebServiceTypes.GetListAllResultType>({
            url: webServiceURL.getListAll,
            params: { ...params, userGroupID },
        })
        return data
    }

    const deleteProjectRequest = async (projectId: number, params: requestWebServiceTypes.DeleteProjectRequest) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.DeleteResponseType>({
            url: `${webServiceURL.project}/${projectId}/delete`,
            params: params,
        })
        return data
    }

    const deleteTempFile = async (params: requestWebServiceTypes.DeleteTempType) => {
        const { data } = await callApi.axiosPost<responseWebServiceTypes.DeleteResponseType>({
            url: webServiceURL.deleteTemp,
            params: params,
        })
        return data
    }

    const specifyReceiveDocumentDate = async (params: requestWebServiceTypes.SpecifyReceiveDocumentDateRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.specifyReceiveDocumentDate,
            params: params,
        })
        return data
    }
    const requestNumberOfDocument = async (params: requestWebServiceTypes.RequestNumberOfDocType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.RequestNumberOfDocumentType>({
            url: webServiceURL.requestNumberOfDocument,
            params: params,
        })
        return data
    }

    //silent = true ใช้กับ autosave
    const commentaryProject = async (params: requestWebServiceTypes.CommentaryProjectType, silent = false) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.CommentaryProjectType>({
            url: webServiceURL.commentaryProject,
            params: params,
            silent: silent,
        })
        return data
    }

    const getCommentaryProjectCommitteeByID = async (submittedID: number, committeeUserID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetCommentaryProjectCommitteeByIDType>({
            url: webServiceURL.getCommentaryProjectCommitteeByID + `/${submittedID}/${committeeUserID}`,
        })
        return data
    }
    const getAllCommentaryProjectCommittee = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetAllCommentaryProjectCommittee>({
            url: webServiceURL.getAllCommentaryProjectCommittee + `/${submittedID}`,
        })
        return data
    }
    const checkAdvisorDueDate = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AdvisorDueDateByIDType>({
            url: `${webServiceURL.checkAdvisorDueDateByID}?submitedID=${submittedID}`,
        })
        return data
    }

    const getSynthesisReview = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.SynthesisReviewType>({
            url: webServiceURL.getSynthesisReview + `/${submittedID}`,
        })
        return data
    }

    const synthesisReviewApproveWithCommentProject = async (param: SynthesisReviewProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.synthesisReviewApproveWithCommentProject,
            params: param,
        })
        return data
    }
    const synthesisReviewProject = async (param: SynthesisReviewProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.synthesisReviewProject,
            params: param,
        })
        return data
    }

    const synthesisReviewProjectRenewal = async (param: SynthesisReviewProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.synthesisReviewProjectRenewal,
            params: param,
        })
        return data
    }

    const synthesisReviewCloseProject = async (param: SynthesisReviewProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.synthesisReviewCloseProject,
            params: param,
        })
        return data
    }

    const synthesisReviewTerminatedProject = async (param: SynthesisReviewProjectRequestType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SpecifyReceiveDocumentDateResponseType>({
            url: webServiceURL.synthesisReviewTerminatedProject,
            params: param,
        })
        return data
    }

    //silent = true ใช้กับ autosave จะไม่เด้ง dialog "บันทึกข้อมูลไม่สำเร็จ" ตอนผู้ใช้กำลังพิมพ์
    const synthesisProject = async (params: requestWebServiceTypes.SynthesisProjectType, silent = false) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SynthesisProjectType>({
            url: webServiceURL.synthesisProject,
            params: params,
            silent: silent,
        })
        return data
    }

    const getSynthesisProjectByID = async (submittedID: Number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetSynthesisProjectByIDType>({
            url: webServiceURL.getSynthesisProjectByID + `/${submittedID}`,
        })
        return data
    }

    const getCertificateByID = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetCertificateByIDType>({
            url: webServiceURL.getCertificateByID + `/${submittedID}`,
        })
        return data
    }

    const certificate = async (params: requestWebServiceTypes.CertificateType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.CertificateType>({
            url: webServiceURL.certificate,
            params: params,
        })
        return data
    }
    const updateLanguage = async (params: requestWebServiceTypes.LanguageParamsType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.LanguageType>({
            url: webServiceURL.languageUpdate,
            params,
        })
        return data
    }
    const updateCertificataData = async (params: requestWebServiceTypes.CertificateDataType) => {
        const result = await callApi.axiosPut<responseWebServiceTypes.CertificateDataResponse>({
            url: webServiceURL.updateCertificateData,
            params,
        })
        return { result }
    }
    const getSignCertificate = async (projectID: number, submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetSignCertificateType>({
            url: webServiceURL.getSignCertificate + `/${projectID}/${submittedID}`,
        })
        return data
    }
    const getSignMemomessage = async (projectID: number, submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetSignMemomessageType>({
            url: webServiceURL.getSignMemomessage + `/${projectID}/${submittedID}`,
        })
        return data
    }
    const signCertificate = async (params: requestWebServiceTypes.SignCertificateType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SignCertificateType>({
            url: webServiceURL.signCertificate,
            params: params,
        })
        return data
    }
    const signMemomessage = async (params: requestWebServiceTypes.SignMemomessageType) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.SignCertificateType>({
            url: webServiceURL.signMemoMessage,
            params: params,
        })
        return data
    }

    const getProjectFolder = async (projectID: number) => {
        const { data } = await callApi.axiosGet<any>({
            url: webServiceURL.getProjectFolderByID + `/${projectID}`,
        })
        return data
    }

    const getFileHistory = async (fileID: number, fileReplaceID: number) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.getFileHistory,
            params: {
                fileID,
                fileReplaceID,
            },
        })
        return data
    }
    const getAdditionalFileHistory = async (fileID: number, fileReplaceID?: number) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.getAdditionalFileHistory,
            params: {
                fileID,
                fileReplaceID,
            },
        })
        return data
    }

    const projectApproveWithComment = async (params: requestWebServiceTypes.EditAfterReviewRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectApproveWithComment,
            params: params,
        })
        return data
    }

    const closeProject = async (params: requestWebServiceTypes.CloseProject) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.closeProject,
            params: params,
        })
        return data
    }

    const closeProjectByAdmin = async (params: requestWebServiceTypes.CloseProjectByAdmin) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.closeProjectByAdmin,
            params: params,
        })
        return data
    }

    const closeProjectByAdminNew = async (params: requestWebServiceTypes.CloseProjectByAdminNew) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.closeProjectByAdmin,
            params: params,
        })
        return data
    }

    const projectAmendmentManagement = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            // url: 'http://localhost:8000/api/project/projectAmendmentManagement', //webServiceURL.projectAmendmentManagement,
            url: webServiceURL.projectAmendmentManagement,
            params: params,
        })
        return data
    }

    const projectRenewalAndAmendmentManagement = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectRenewalAndAmendmentManagement,
            params: params,
        })
        return data
    }

    const projectPhaseManagement = async (params: requestWebServiceTypes.ProjectPhaseRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectPhase,
            params: params,
        })
        return data
    }

    const projectRenewalManagement = async (params: requestWebServiceTypes.ProjectRenewalRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectRenewal,
            params: params,
        })
        return data
    }
    const terminateProject = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.terminateProject,
            params: params,
        })
        return data
    }

    const terminateProjectByAdmin = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectTerminateByAdmin,
            params: params,
        })
        return data
    }

    const adverseEvent = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectAdverseEvent,
            params: params,
        })
        return data
    }

    const projectProgressReport = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectProgressReport,
            params: params,
        })
        return data
    }

    const projectSiteVisit = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectSiteVisit,
            params: params,
        })
        return data
    }

    const submitProjectSiteVisit = async (params: requestWebServiceTypes.CreateProjectRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.submitProjectSiteVisit,
            params: params,
        })
        return data
    }

    const reportedAdverseEvents = async (params: requestWebServiceTypes.ReportedAdverseEventsRequestType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.reportedAdverseEvents,
            params: params,
        })
        return data
    }
    const projectTerminateMemoMessage = async (params: requestWebServiceTypes.ProjectTerminateMemomessageType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectTerminateMemoMessage,
            params: params,
        })
        return data
    }

    const getProjectTerminateMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.TerminateMemoMessageResponse>({
            url: webServiceURL.getProjectTerminateMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const getProjectNonconformanceMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.ProjectNonconformanceMemoMessageResponse>({
            url: webServiceURL.getProjectNonconformanceMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const projectNonconformanceMemoMessage = async (
        params: requestWebServiceTypes.ProjectNonconformanceMemoMessageType,
    ) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectNonconformanceMemoMessage,
            params: params,
        })
        return data
    }

    const closeProjectByAdminMemoMessage = async (
        params: requestWebServiceTypes.CloseProjectByAdminMemoMessageType,
    ) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.closeProjectByAdminMemoMessage,
            params: params,
        })
        return data
    }

    const getCloseProjectByAdminMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AdverseEventMemoMessageResponse>({
            url: webServiceURL.getCloseProjectbyAdminMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const projectAdverseEventMemoMessage = async (
        params: requestWebServiceTypes.ProjectAdverseEventMemomessageType,
    ) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectAdverseEventMemoMessage,
            params: params,
        })
        return data
    }

    const projectProgressReportMemoMessage = async (
        params: requestWebServiceTypes.ProjectAdverseEventMemomessageType,
    ) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectProgressReportMemoMessage,
            params: params,
        })
        return data
    }

    const projectSiteVisitMemoMessage = async (params: requestWebServiceTypes.ProjectAdverseEventMemomessageType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectSiteVisitMemoMessage,
            params: params,
        })
        return data
    }

    const getProjectAdverseEventMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AdverseEventMemoMessageResponse>({
            url: webServiceURL.getProjectAdverseEventMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const getProjectProgressReportMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AdverseEventMemoMessageResponse>({
            url: webServiceURL.getProjectProgressReportMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const getProjectSiteVisitMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AdverseEventMemoMessageResponse>({
            url: webServiceURL.getProjectSiteVisitMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const projectAmendmentMemoMessage = async (params: requestWebServiceTypes.ProjectAmendmentMemomessageType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectAmendmentMemoMessage,
            params: params,
        })
        return data
    }

    const projectNotPeopleMemoMessage = async (params: requestWebServiceTypes.ProjectNotPeopleMemoMessageType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.projectNotPeopleMemoMessage,
            params: params,
        })
        return data
    }

    const getProjectAmendmentMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.AmendmentMemoMessageResponse>({
            url: webServiceURL.getProjectAmendmentMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const getProjectNotPeopleMemoMessage = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.ProjectNotPeopleMemoMessageResponse>({
            url: webServiceURL.getProjectNotPeopleMemoMessage + `/${submittedID}`,
        })
        return data
    }

    const updateMemoMessagePaper = async (params: requestWebServiceTypes.UpdateMemoMessageType) => {
        const result = await callApi.axiosPut<responseWebServiceTypes.UpdateMemoMessageResponse>({
            url: webServiceURL.updateMemoMessage,
            params,
        })
        return { result }
    }

    const getCertificateCloseProject = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.CertificateCloseProjectResponse>({
            url: webServiceURL.getCertificateCloseProject + `/${submittedID}`,
        })
        return data
    }

    const certificateCloseProject = async (params: requestWebServiceTypes.CertificateCloseProjectType) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.certificateCloseProject,
            params: params,
        })
        return data
    }

    const getNotificationInfo = async (userID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetNotificationInfoResponse>({
            url: webServiceURL.getNotificationInfo + `/${userID}`,
        })
        return data
    }

    const deleteNotificationInfo = async (notiID: number) => {
        const { data } = await callApi.axiosDelete<responseWebServiceTypes.DeleteNotificationInfoResponse>({
            url: webServiceURL.deleteNotificationInfo + `/${notiID}`,
        })
        return data
    }

    const updateWatchedNotification = async (params: { notiID: number }) => {
        const { data } = await callApi.axiosPut<any>({
            url: webServiceURL.updateWatchedNotification,
            params: params,
        })
        return data
    }

    const getProjectPageUpdater = async (projectID: number, submittedID: number, stepID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetUpdaterPage>({
            url: `${webServiceURL.getProjectPageUpdater}/${projectID}/${submittedID}/${stepID}`,
        })
        return data
    }

    const deleteProject = async (projectID: number) => {
        const { data } = await callApi.axiosDelete<responseWebServiceTypes.DeleteProjectResponse>({
            url: `${webServiceURL.deleteProject}/${projectID}`,
        })
        return data
    }

    const saveProjectWithdraw = async (params: requestWebServiceTypes.SaveProjectWithdrawType) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.saveProjectWithdraw,
            params: params,
        })
        return data
    }

    const saveProjectWithdrawResubmit = async (params: requestWebServiceTypes.SaveProjectWithdrawType) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.saveProjectWithdrawResubmit,
            params: params,
        })
        return data
    }

    const saveVerifyProjectWithdraw = async (params: requestWebServiceTypes.SaveVerifyProjectWithdrawType) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.saveVerifyProjectWithdraw,
            params: params,
        })
        return data
    }

    const saveConfirmProjectWithdraw = async (params: requestWebServiceTypes.SaveConfirmProjectWithdraw) => {
        const { data } = await callApi.axiosPost<any>({
            url: webServiceURL.saveConfirmProjectWithdraw,
            params: params,
        })
        return data
    }

    const getProjectWithdraw = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<
            responseWebServiceTypes.ServiceResult<responseWebServiceTypes.GetProjectWithdrawResponse>
        >({
            url: `${webServiceURL.getProjectWithdraw}/${submittedID}`,
        })
        return data
    }

    const updateProjectDetail = async (params: UpdateProjectDetailType) => {
        const { data } = await callApi.axiosPut({
            url: webServiceURL.updateProjectDetail,
            params: params,
        })
        return data
    }

    const addPendingAndResume = async (params: PendingAndResumeType) => {
        const { data } = await callApi.axiosPut({
            url: webServiceURL.addPendingAndResume,
            params: params,
        })
        return data
    }

    const getHistoryPendingAndResume = async (submittedID: number) => {
        const { data } = await callApi.axiosGet({
            url: `${webServiceURL.getHistoryPendingAndResume}/${submittedID}`,
        })
        return data
    }

    const updateProjectConsiderType = async (params: requestWebServiceTypes.UpdateProjectConsiderType) => {
        const response = await callApi.axiosPut<responseWebServiceTypes.SpecifyProjectTypeType>({
            url: webServiceURL.updateProjectConsiderType,
            params: params,
        })
        return response
    }

    const updateProjectReviewer = async (params: requestWebServiceTypes.UpdateProjectReviewer) => {
        const response = await callApi.axiosPut<responseWebServiceTypes.SpecifyProjectTypeType>({
            url: webServiceURL.updateProjectReviewer,
            params: params,
        })
        return response
    }

    const updateSynthesisApproveStatus = async (params: Partial<requestWebServiceTypes.SynthesisProjectType>) => {
        const response = await callApi.axiosPut({
            url: webServiceURL.updateSynthesisApproveStatus,
            params: params,
        })
        return response
    }

    const updateSynthesisCommentaryTopic = async (params: any) => {
        const response = await callApi.axiosPut({
            url: webServiceURL.updateSynthesisCommentaryTopic,
            params: params,
        })
        return response
    }

    const getConsiderTypeHistory = async (submittedId: number) => {
        const response = await callApi.axiosGet({
            url: `${webServiceURL.getConsiderTypeHistory}?submittedID=${submittedId}`,
        })
        return response
    }

    //silent = true ใช้กับ autosave
    const submitAllCheckList = async (params: requestWebServiceTypes.CheckListCommentaryType, silent = false) => {
        const { data } = await callApi.axiosPost({
            url: webServiceURL.checkListCommentary,
            params,
            silent: silent,
        })
        return data
    }

    const getHistoryCommentaryProjectCommittee = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetCommentaryForExport>({
            url: `${webServiceURL.getHistoryCommentaryProjectCommittee}/${submittedID}`,
        })
        return data
    }

    const getHistorySynthesis = async (submittedID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetCommentaryForExport>({
            url: `${webServiceURL.getHistorySynthesis}/${submittedID}`,
        })
        return data
    }

    const submitOnlyDocReceiveDate = async (params: requestWebServiceTypes.SubmitOnlyDocumentReceiveDate) => {
        const { data } = await callApi.axiosPut<responseWebServiceTypes.ServiceResult<any>>({
            url: webServiceURL.submitOnlyDocReceiveDate,
            params: params,
        })
        return data
    }

    const getUserHistoryUpload = async (fileID: number | null, userID: number) => {
        const { data } = await callApi.axiosGet<responseWebServiceTypes.GetUserHistoryUpload>({
            url: `${webServiceURL.getHistoryUpload}/${fileID}/${userID}`,
        })
        return data
    }

    const getProjectForm = async (submittedTypeId: number, irb: number) => {
        const { data } = await callApi.axiosGet<{
            status: number
            message: string
            result: requestWebServiceTypes.DocumentTopic[]
        }>({
            url: `${webServiceURL.projectForm}/${submittedTypeId}/${irb}`,
        })
        return data
    }
    const getExampleInfomation = async (userGroupId: number) => {
        const { data } = await callApi.axiosGet<{
            status: number
            message: string
            result: ExampleInfomation[]
        }>({
            url: `${webServiceURL.exampleInfomation}/${userGroupId}`,
        })
        return data
    }

    const getProjectDashboard = async (userGroupId: string, submittedStatuses?: string[]) => {
        const base = webServiceURL.projectDashboard
        const u = new URL(base, window.location.origin)
        u.searchParams.set('userGroupId', String(userGroupId))
        if (submittedStatuses?.length) {
            submittedStatuses.forEach((s) => u.searchParams.append('submittedStatuses', String(s)))
        }

        const { data } = await callApi.axiosGet<responseWebServiceTypes.ProjectDashboard>({
            url: u.toString(),
        })
        return data
    }

    return {
        example1,
        example2,
        createNewProject,
        updateFileProject,
        updateFileStamp,
        uploadFileTemp,
        getProjectByID,
        verifyProject,
        specifyProjectType,
        searchTbl,
        searchTblMeeting,
        getProjectReviewSummary,
        deleteTempFile,
        specifyReceiveDocumentDate,
        requestNumberOfDocument,
        commentaryProject,
        getCommentaryProjectCommitteeByID,
        checkAdvisorDueDate,
        getSynthesisReview,
        synthesisReviewApproveWithCommentProject,
        synthesisReviewProject,
        synthesisProject,
        getSynthesisProjectByID,
        getCertificateByID,
        certificate,
        updateLanguage,
        signCertificate,
        getSignCertificate,
        getProjectFolder,
        projectApproveWithComment,
        closeProject,
        closeProjectByAdmin,
        projectAmendmentManagement,
        projectRenewalAndAmendmentManagement,
        projectPhaseManagement,
        projectRenewalManagement,
        verifyProjectRenewal,
        reportedAdverseEvents,
        synthesisReviewProjectRenewal,
        terminateProject,
        verifyProjectTerminate,
        projectTerminateMemoMessage,
        getProjectTerminateMemoMessage,
        signMemomessage,
        adverseEvent,
        verifyProjectAdverseEvent,
        projectAdverseEventMemoMessage,
        getProjectAdverseEventMemoMessage,
        getSignMemomessage,
        getProjectNonconformanceMemoMessage,
        projectNonconformanceMemoMessage,
        projectAmendmentMemoMessage,
        projectNotPeopleMemoMessage,
        getProjectAmendmentMemoMessage,
        getProjectNotPeopleMemoMessage,
        getFileHistory,
        getAdditionalFileHistory,
        synthesisReviewCloseProject,
        getCertificateCloseProject,
        certificateCloseProject,
        verifyEditAfterReviewCloseProject,
        getNotificationInfo,
        deleteNotificationInfo,
        updateWatchedNotification,
        getAllCommentaryProjectCommittee,
        synthesisReviewTerminatedProject,
        terminateProjectByAdmin,
        closeProjectByAdminNew,
        closeProjectByAdminMemoMessage,
        getCloseProjectByAdminMemoMessage,
        getProjectPageUpdater,
        deleteProject,
        saveProjectWithdraw,
        saveProjectWithdrawResubmit,
        getProjectWithdraw,
        saveVerifyProjectWithdraw,
        saveConfirmProjectWithdraw,
        updSubmittedCommittee,
        updateProjectDetail,
        addPendingAndResume,
        updateCertificataData,
        getHistoryPendingAndResume,
        updateProjectConsiderType,
        updateMemoMessagePaper,
        updateProjectReviewer,
        updateSynthesisApproveStatus,
        updateSynthesisCommentaryTopic,
        getConsiderTypeHistory,
        submitAllCheckList,
        getHistoryCommentaryProjectCommittee,
        submitOnlyDocReceiveDate,
        getHistorySynthesis,
        getUserHistoryUpload,
        getProjectForm,
        deleteProjectRequest,
        getExampleInfomation,
        getProjectDashboard,
        projectProgressReport,
        getProjectProgressReportMemoMessage,
        verifyProjectProgressReport,
        projectProgressReportMemoMessage,
        projectSiteVisit,
        projectSiteVisitMemoMessage,
        getProjectSiteVisitMemoMessage,
        verifyProjectSiteVisit,
        submitProjectSiteVisit,
    }
}

export default useWebService
